import { CHAR_LIMITS } from "../constants/templateCreatorConstants";

export const validateTemplateName = (name) => {
  if (!name || !name.trim()) return "Template name is required";
  if (name.toLowerCase() === "test") return "Name 'test' is not allowed";
  if (!/^[a-z0-9_]+$/.test(name))
    return "Only lowercase letters, numbers, and underscores allowed";
  if (name.length > CHAR_LIMITS.TEMPLATE_NAME)
    return `Name cannot exceed ${CHAR_LIMITS.TEMPLATE_NAME} characters`;
  return null;
};

export const validateFooter = (footer) => {
  if (!footer || !footer.trim()) return null;
  if (footer.includes("{{") || footer.includes("}}"))
    return "No variables allowed in footer";
  if (footer.length > CHAR_LIMITS.FOOTER)
    return `Footer cannot exceed ${CHAR_LIMITS.FOOTER} characters`;
  return null;
};

export const validateHeaderText = (text, variables) => {
  if (!text || !text.trim()) return null;
  if (text.length > CHAR_LIMITS.HEADER_TEXT)
    return `Header cannot exceed ${CHAR_LIMITS.HEADER_TEXT} characters`;
  if (variables && variables.length > 1)
    return "Only 1 variable allowed in header";
  return null;
};

const extractNumberedVars = (text) => {
  const matches = (text || "").match(/\{\{(\d+)\}\}/g) || [];
  return matches
    .map((m) => parseInt(m.replace(/[{}]/g, ""), 10))
    .filter((n) => !isNaN(n) && n > 0);
};

const extractNamedVars = (text) => {
  const matches = (text || "").match(/\{\{([a-zA-Z_][a-zA-Z0-9_]*)\}\}/g) || [];
  return matches.map((m) => m.replace(/[{}]/g, ""));
};

export const validateBodyNumbered = (body) => {
  if (!body || typeof body !== "string") return null;
  const trimmed = body.trim();
  if (!trimmed) return "Message body is required";

  if (body.length > CHAR_LIMITS.BODY)
    return `Message body cannot exceed ${CHAR_LIMITS.BODY} characters`;

  const allBraceMatches = body.match(/\{\{[^}]*\}\}/g);
  if (allBraceMatches) {
    for (const m of allBraceMatches) {
      if (!/^\{\{\d+\}\}$/.test(m))
        return "Invalid variable format. Use {{1}}, {{2}}, etc. Only numbers allowed.";
    }
  }

  if (/\{\d+\}/.test(body) && !/\{\{\d+\}\}/.test(body))
    return "Invalid variable format. Use {{1}}, {{2}} with double braces.";
  if (/\{\{\s+\d+\s*\}\}|\{\{\s*\d+\s+\}\}/.test(body))
    return "No spaces inside braces. Use {{1}} not {{ 1 }}.";
  if (/\{\{0\d+\}\}/.test(body))
    return "Invalid variable format. Use {{1}} not {{01}}.";

  if (/^\s*\{\{\d+\}\}\s*$/.test(trimmed))
    return "A variable cannot be the only content. Add text around it.";

  if (/^\s*\{\{\d+\}\}/.test(trimmed))
    return "Variables cannot be at the start of the template. Add text before the variable.";
  if (/\{\{\d+\}\}\s*$/.test(trimmed))
    return "Variables cannot be at the end of the template. Add text after the variable.";

  const varNums = extractNumberedVars(body);
  const unique = [...new Set(varNums)].sort((a, b) => a - b);
  for (let i = 0; i < unique.length; i++) {
    if (unique[i] !== i + 1)
      return "Variables must be sequential from {{1}}. Cannot skip numbers.";
  }

  if (/(\{\{\d+\}\}\s*){5,}/.test(body))
    return "Avoid too many consecutive variables. Add text between them.";

  const varMatches = body.match(/\{\{\d+\}\}/g) || [];
  const varCharCount = varMatches.reduce((sum, m) => sum + m.length, 0);
  if (body.length > 0 && varCharCount / body.length > 0.8)
    return "Variables should not be most of the message. Add more text.";

  if (unique.length > 20)
    return "Maximum 20 variables recommended. Too many may lead to rejection.";

  return null;
};

export const validateBodyNamed = (body) => {
  if (!body || typeof body !== "string") return null;
  const trimmed = body.trim();
  if (!trimmed) return "Message body is required";

  if (body.length > CHAR_LIMITS.BODY)
    return `Message body cannot exceed ${CHAR_LIMITS.BODY} characters`;

  const allBraceMatches = body.match(/\{\{[^}]*\}\}/g);
  if (allBraceMatches) {
    for (const m of allBraceMatches) {
      if (!/^\{\{[a-zA-Z_][a-zA-Z0-9_]*\}\}$/.test(m))
        return `Invalid variable format: ${m}. Use {{param_name}} with valid identifiers (letters, numbers, underscores, starting with a letter or underscore).`;
    }
  }

  if (/\{\{\s+[^}]*\}\}|\{\{[^}]*\s+\}\}/.test(body))
    return "No spaces inside braces. Use {{name}} not {{ name }}.";

  if (/^\s*\{\{[a-zA-Z_][a-zA-Z0-9_]*\}\}\s*$/.test(trimmed))
    return "A variable cannot be the only content. Add text around it.";

  if (/^\s*\{\{[a-zA-Z_]/.test(trimmed))
    return "Variables cannot be at the start of the template. Add text before the variable.";

  const namedEndRegex = /\{\{[a-zA-Z_][a-zA-Z0-9_]*\}\}\s*$/;
  if (namedEndRegex.test(trimmed))
    return "Variables cannot be at the end of the template. Add text after the variable.";

  const varMatches = body.match(/\{\{[a-zA-Z_][a-zA-Z0-9_]*\}\}/g) || [];
  const varCharCount = varMatches.reduce((sum, m) => sum + m.length, 0);
  if (body.length > 0 && varCharCount / body.length > 0.8)
    return "Variables should not be most of the message. Add more text.";

  if (varMatches.length > 20)
    return "Maximum 20 variables recommended. Too many may lead to rejection.";

  return null;
};

export const validateBody = (body, variableType) => {
  if (variableType === "named") return validateBodyNamed(body);
  return validateBodyNumbered(body);
};

export const validateButtonText = (text) => {
  if (!text || !text.trim()) return "Button text is required";
  if (text.length > CHAR_LIMITS.BUTTON_TEXT)
    return `Button text cannot exceed ${CHAR_LIMITS.BUTTON_TEXT} characters`;
  return null;
};

export const validateUrlButton = (button) => {
  const errors = {};
  const textErr = validateButtonText(button.text);
  if (textErr) errors.text = textErr;

  if (!button.url || !button.url.trim()) {
    errors.url = "Website URL is required";
  } else if (button.url.length > CHAR_LIMITS.WEBSITE_URL) {
    errors.url = `URL cannot exceed ${CHAR_LIMITS.WEBSITE_URL} characters`;
  } else if (button.url_type === "dynamic") {
    const varMatches = button.url.match(/\{\{\d+\}\}/g) || [];
    if (varMatches.length === 0) {
      errors.url = "Dynamic URL must contain {{1}} variable";
    } else if (varMatches.length > 1) {
      errors.url = "Only one variable allowed in URL";
    } else if (!button.url.endsWith("{{1}}")) {
      errors.url = "Variable {{1}} must appear at the end of the URL";
    }
  }

  return Object.keys(errors).length > 0 ? errors : null;
};

export const validatePhoneButton = (button) => {
  const errors = {};
  const textErr = validateButtonText(button.text);
  if (textErr) errors.text = textErr;

  if (!button.phone_number || !button.phone_number.trim()) {
    errors.phone_number =
      "You need to enter a phone number. Please add a valid phone number.";
  } else if (!/^\d+$/.test(button.phone_number.replace(/\s/g, ""))) {
    errors.phone_number = "Phone number must be numeric";
  } else if (button.phone_number.length > CHAR_LIMITS.PHONE_NUMBER) {
    errors.phone_number = `Phone number cannot exceed ${CHAR_LIMITS.PHONE_NUMBER} characters`;
  }

  return Object.keys(errors).length > 0 ? errors : null;
};

export const validateOfferCodeButton = (button) => {
  const errors = {};
  const textErr = validateButtonText(button.text);
  if (textErr) errors.text = textErr;

  if (!button.offer_code || !button.offer_code.trim()) {
    errors.offer_code = "Offer code is required";
  } else if (button.offer_code.length > CHAR_LIMITS.OFFER_CODE) {
    errors.offer_code = `Offer code cannot exceed ${CHAR_LIMITS.OFFER_CODE} characters`;
  }

  return Object.keys(errors).length > 0 ? errors : null;
};

export const validateFlowButton = (button) => {
  const errors = {};
  const textErr = validateButtonText(button.text);
  if (textErr) errors.text = textErr;
  if (!button.flow_id) errors.flow_id = "Flow selection is required";
  return Object.keys(errors).length > 0 ? errors : null;
};

export const detectVariablesInText = (text, variableType) => {
  if (!text) return [];
  if (variableType === "named") {
    return extractNamedVars(text);
  }
  return [...new Set(extractNumberedVars(text))].sort((a, b) => a - b);
};
